// @flow

import React from 'react';
import { I18n, Translate } from 'react-redux-i18n';
import { all, LU, MSU, SU } from '../../../../const';
import type { BranchLevels } from '../../../../types/shoppingCenterType';
import CurrencySurfaceSwitcher from '../../../common/CurrencySurfaceSwitcher';
import SearchSelectStore from './SearchSelectStore';
import geminiApi from '../../../../network/api/geminiApi';
import { download } from '../../../../utils/utils';
import { getCurrentPcId } from '../../../../utils/urlUtils';
import IconButton from '../../../../components/IconButton';
import type { VisioglobeUnitsOk, YearMonth } from '../../../../types/geminiType';
import { trackAction } from '../../../../components/GoogleAnalyticsWrapper';
import GeminiPOVFilter from './GeminiPOVFilter';
import { notifyApolloError } from '../../../../network/notification';

type Props = {
  dispatch: () => void,
  isTabCollapsed: boolean,
  collapseTab: Function,
  yearMonth: YearMonth,
  selectedCategory: string,
  visioglobeUnits: VisioglobeUnitsOk[],
  branchLevels: BranchLevels[],
  updateSelectedBranchLevel: Function,
  selectedZone: string[],
  updateSelectedZone: Function,
  updateSelectedStore: (string, VisioglobeUnitsOk) => void,
  selectedBranches: number[],
  zoneColorMap: Object;
  selectStoreSize: Function,
  selectShoppingCenterCat: Function,
  selectBranchCat: Function,
  selectZoneCat: Function,
  selectStoreCat: Function,
  storeCategory: string,
  clearFilter: Function,
  id?: string,
  isUp?: boolean,
  disableActionPOV: boolean,
  isPulseAuthorized: boolean,
}

type State = {
  search: string,
  isM5Loading: boolean,
}
const DL_M5_1 = 'DL_M5.1';

class GeminiLeftTab extends React.Component<Props, State> {
  static defaultProps = {
    id: '',
    isUp: false,
  };

  state: State = {
    search: '',
    isM5Loading: false,
  };

  constructor() {
    super();
    this.inputSearch = React.createRef();
    this.clearFilter = this.clearFilter.bind(this);
  }

  clearFilter = () => {
    this.props.clearFilter();
    this.setState({ search: '' });
    this.inputSearch.current.value = '';
  };

  fetchM5Report = () => {
    const currentPcId = getCurrentPcId();
    this.setState({
      isM5Loading: true,
    });
    trackAction(DL_M5_1);
    geminiApi.fetchM5Report(
      currentPcId,
      this.props.yearMonth.year,
      this.props.yearMonth.month,
    ).then(response => response.blob())
      .then(responseBlob => download(
        responseBlob,
        I18n.t('SIDE_MENU.m5_report_filename', { scName: currentPcId }),
      )).catch(e => this.props.dispatch(notifyApolloError(e)))
      .finally(() => this.setState({ isM5Loading: false }));
  };

  render() {
    return (
      <div className={`left-filters-container${this.props.isUp ? ' isUp' : ''}`} id={this.props.id}>
        <div className="search-bar">
          <SearchSelectStore
            searchWord={this.state.search}
            selectSources={
              this.props.visioglobeUnits
                .map(store => ({
                  label: store.storeName,
                  id: store.idVisioglobes[0],
                  store,
                }))
            }
            search={(event: EventHandler) => {
              this.setState({
                search: event.target.value,
              });
            }}
            handleChange={this.props.updateSelectedStore}
            selectStoreCat={this.props.selectStoreCat}
            resetSearch={() => this.setState({ search: '' })}
            reference={this.inputSearch}
          />
        </div>
        <div
          role="presentation"
          onClick={this.props.isTabCollapsed ? this.props.collapseTab : null}
          className={this.props.isTabCollapsed ? 'exit-button side-data-gemini-hidden left-menu-gemini-hidden side-data-container' : 'side-data-gemini side-data-container filters search-button'}
        >
          {
            !this.props.isTabCollapsed ?
              <div className="gemini-store-data-container filter-first-step">
                <div className="placeholder">
                  <button onClick={this.props.collapseTab} className="left-exit-button">
                    <i className="fal fa-chevron-left left-chevron" />
                  </button>
                </div>
                <div className="top-menu-gemini-cell available-reports">
                  <div className="data-section-header">
                    <Translate value="SIDE_MENU.report" />
                  </div>
                  <div className="selector-buttons-row">
                    <div className="action-buttons-container">
                      <div className="btn-apollo-container">
                        <IconButton
                          icon="fal fa-file-download"
                          className={`ripple-button apollo-button-contained${this.state.isM5Loading ? ' disabled' : ''}`}
                          isLoading={this.state.isM5Loading}
                          onClick={() => {
                            this.fetchM5Report();
                          }}
                          disabled={!this.props.isPulseAuthorized}
                          title={!this.props.isPulseAuthorized && I18n.t('SIDE_MENU.report_unauthorized')}
                        >
                          <Translate value="SIDE_MENU.m5_report" />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="top-menu-gemini-cell">
                  <div className="data-section-header">
                    <Translate value="SIDE_MENU.unit_size" />
                  </div>
                  <div className="selector-buttons-row">
                    <button
                      onClick={() => this.props.selectStoreSize(all)}
                      className={`selector-buttons ${this.props.storeCategory && this.props.storeCategory === all ? 'selected' : ''}`}
                    >
                      <Translate value="GEMINI_HEADER.all" />
                    </button>
                    <button
                      onClick={() => this.props.selectStoreSize(SU.label)}
                      className={`selector-buttons ${this.props.storeCategory && this.props.storeCategory === SU.label.toLowerCase() ? 'selected' : ''}`}
                    >
                      <Translate value="GEMINI_HEADER.su" />
                    </button>
                    <button
                      onClick={() => this.props.selectStoreSize(MSU.label)}
                      className={`selector-buttons ${this.props.storeCategory && this.props.storeCategory === MSU.label.toLowerCase() ? 'selected' : ''}`}
                    >
                      <Translate value="GEMINI_HEADER.msu" />
                    </button>
                    <button
                      onClick={() => this.props.selectStoreSize(LU.label)}
                      className={`selector-buttons ${this.props.storeCategory && this.props.storeCategory === LU.label.toLowerCase() ? 'selected' : ''}`}
                    >
                      <Translate value="GEMINI_HEADER.lu" />
                    </button>
                  </div>
                </div>
                <div className="top-menu-gemini-cell">
                  <div className="data-section-header">
                    <Translate value="SIDE_MENU.currency" />
                  </div>
                  <CurrencySurfaceSwitcher displaySelector />
                </div>
                <GeminiPOVFilter
                  visioglobeUnits={this.props.visioglobeUnits}
                  branchLevels={this.props.branchLevels}
                  zoneColorMap={this.props.zoneColorMap}
                  selectedCategory={this.props.selectedCategory}
                  selectedZone={this.props.selectedZone}
                  selectedBranches={this.props.selectedBranches}
                  selectShoppingCenterCat={this.props.selectShoppingCenterCat}
                  selectBranchCat={this.props.selectBranchCat}
                  selectZoneCat={this.props.selectZoneCat}
                  selectStoreCat={this.props.selectStoreCat}
                  updateSelectedZone={this.props.updateSelectedZone}
                  updateSelectedBranchLevel={this.props.updateSelectedBranchLevel}
                  updateSelectedStore={this.props.updateSelectedStore}
                  clearFilter={this.clearFilter}
                  disableAction={this.props.disableActionPOV}
                />
              </div>
              :
              <i className="fal fa-chevron-right right-chevron" />
          }
        </div>
      </div>
    );
  }
}

export default GeminiLeftTab;
